/**
 * GRANULLO V1.1 — Global System Status Bar
 * Institutional infrastructure status indicator with system flow
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Activity, BookOpen } from 'lucide-react';

// System flow stages
const FLOW_STAGES = [
  { id: 'intent', label: 'Intent', paths: ['/', '/homepage'] },
  { id: 'commitment', label: 'Commitment', paths: ['/registry', '/granullo-atlas', '/atlas'] },
  { id: 'simulation', label: 'Simulation', paths: ['/simulation'] },
  { id: 'cascade', label: 'Cascade', paths: ['/simulation'] },
  { id: 'memory', label: 'Memory', paths: ['/simulation'] },
  { id: 'exposure', label: 'Exposure', paths: ['/mission-control', '/observatory'] },
  { id: 'intervention', label: 'Intervention', paths: ['/observatory'] }
];

export function GlobalSystemStatusBar() {
  const location = useLocation();
  const [commitmentCount, setCommitmentCount] = useState(1247);
  const [exposureIndex, setExposureIndex] = useState('moderate');
  
  // Determine active stage
  const getActiveStage = () => {
    const currentPath = location.pathname;
    for (const stage of FLOW_STAGES) {
      if (stage.paths.some(p => currentPath.includes(p) || currentPath === p)) {
        return stage.id;
      }
    }
    return 'intent';
  };
  
  const activeStage = getActiveStage();
  const activeIndex = FLOW_STAGES.findIndex(s => s.id === activeStage);
  
  // Simulate live data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCommitmentCount(prev => prev + Math.floor(Math.random() * 3) - 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const getExposureColor = () => {
    switch (exposureIndex) {
      case 'low': return '#3fb950';
      case 'moderate': return '#f0883e';
      case 'high': return '#f85149';
      default: return '#8b949e';
    }
  };
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '28px',
        background: 'var(--g-bg-tertiary, #0d1117)',
        borderBottom: '1px solid var(--g-border, #21262d)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 9998,
        fontFamily: 'var(--g-font-mono, monospace)',
        fontSize: '10px',
        letterSpacing: '0.5px'
      }}
      data-testid="global-system-status-bar"
    >
      {/* Left: Version and Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'var(--g-text-tertiary, #6e7681)' }}>GRANULLO</span>
          <span style={{ color: 'var(--g-text-secondary, #8b949e)', fontWeight: 600 }}>V1.1</span>
        </div>
        
        <div style={{ width: '1px', height: '12px', background: 'var(--g-border, #30363d)' }} />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ 
            width: '6px', 
            height: '6px', 
            borderRadius: '50%', 
            background: '#3fb950',
            boxShadow: '0 0 4px #3fb950'
          }} />
          <span style={{ color: '#3fb950', fontWeight: 600 }}>OPERATIONAL</span>
        </div>
        
        <div style={{ width: '1px', height: '12px', background: 'var(--g-border, #30363d)' }} />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'var(--g-text-tertiary, #6e7681)' }}>COMMITMENTS:</span>
          <span style={{ color: 'var(--g-text-primary, #f0f6fc)', fontWeight: 600 }}>
            {commitmentCount.toLocaleString()}
          </span>
        </div>
      </div>
      
      {/* Center: System Flow Indicator */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '3px',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)'
      }}>
        {FLOW_STAGES.map((stage, index) => {
          const isActive = stage.id === activeStage;
          const isPast = index < activeIndex;
          
          return (
            <React.Fragment key={stage.id}>
              <span
                style={{
                  padding: '1px 5px',
                  borderRadius: '3px',
                  background: isActive ? 'var(--g-accent, #58a6ff)' : 'transparent',
                  color: isActive 
                    ? '#ffffff' 
                    : isPast 
                      ? 'var(--g-text-secondary, #8b949e)' 
                      : 'var(--g-text-tertiary, #484f58)',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '8px',
                  textTransform: 'uppercase',
                  transition: 'all 0.3s ease'
                }}
              >
                {stage.label}
              </span>
              {index < FLOW_STAGES.length - 1 && (
                <span style={{ 
                  color: isPast ? 'var(--g-text-tertiary)' : 'var(--g-border, #30363d)',
                  fontSize: '7px'
                }}>
                  →
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Right: Exposure, Mode, and Guided Tour */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Activity size={10} style={{ color: getExposureColor() }} />
          <span style={{ color: 'var(--g-text-tertiary, #6e7681)' }}>EXPOSURE:</span>
          <span style={{ color: getExposureColor(), fontWeight: 600, textTransform: 'uppercase' }}>
            {exposureIndex}
          </span>
        </div>
        
        <div style={{ width: '1px', height: '12px', background: 'var(--g-border, #30363d)' }} />
        
        {/* Start Guided Tour Button */}
        <button
          onClick={() => {
            localStorage.removeItem('granullo_walkthrough_completed');
            window.location.reload();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            background: 'transparent',
            border: '1px solid var(--g-border, #30363d)',
            borderRadius: '4px',
            cursor: 'pointer',
            color: 'var(--g-text-secondary, #8b949e)',
            fontSize: '9px',
            fontWeight: 500,
            transition: 'all 0.2s ease'
          }}
          data-testid="navbar-guided-tour-btn"
          title="Start Guided Tour"
          onMouseEnter={(e) => {
            e.target.style.background = 'var(--g-bg-secondary, #161b22)';
            e.target.style.color = 'var(--g-accent, #58a6ff)';
            e.target.style.borderColor = 'var(--g-accent, #58a6ff)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = 'var(--g-text-secondary, #8b949e)';
            e.target.style.borderColor = 'var(--g-border, #30363d)';
          }}
        >
          <BookOpen size={10} />
          <span>Guided Tour</span>
        </button>
        
        <div style={{ width: '1px', height: '12px', background: 'var(--g-border, #30363d)' }} />

        <span style={{
          color: 'var(--g-text-secondary, #8b949e)',
          padding: '1px 6px',
          background: 'var(--g-bg-secondary, #161b22)',
          borderRadius: '3px',
          fontWeight: 500
        }}>
          PILOT
        </span>
      </div>
    </div>
  );
}

export default GlobalSystemStatusBar;
